import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { calFetch, localYmd } from "@/lib/calApi";
import { CalLayout } from "@/components/calendar/CalLayout";
import { CalBadge, EventRow } from "@/components/calendar/EventCard";

const WD = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const DOT = { cumple: "bg-amber-deep", aniversario: "bg-amber-deep", especial: "bg-amber-deep", yahrzeit: "bg-night", fiesta: "bg-dusk" };

const SHORT = { Tishrei: "Tish", "Jeshván": "Jesh", Kislev: "Kis", Tevet: "Tev", Shvat: "Shv", "Nisán": "Nis", "Siván": "Siv", Tamuz: "Tam" };
const shortHeb = (s) => s.replace(/^(\d+) (.+)$/, (_, n, m) => `${n} ${SHORT[m] || m}`);

function DayCell({ d, selected, today, onClick }) {
  const kinds = [...new Set(d.events.map((e) => e.kind).filter((k) => DOT[k]))].slice(0, 3);
  const hasCumple = d.events.some((e) => e.kind === "cumple");
  return (
    <button data-testid={`cal-day-${d.date}`} onClick={onClick}
      className={`aspect-square sm:aspect-[1/0.95] rounded-2xl p-1.5 flex flex-col items-center justify-between transition-colors border ${selected ? "bg-night text-white border-night" : hasCumple ? "bg-amber-deep/10 border-amber-deep/30 text-cocoa" : "bg-cream/70 border-line/60 text-cocoa hover:border-amber-deep/50"}`}>
      <span className={`text-sm sm:text-base font-semibold ${today && !selected ? "w-7 h-7 rounded-full bg-candle text-night flex items-center justify-center" : ""}`}>{Number(d.date.slice(8))}</span>
      <span className={`text-[9px] sm:text-[10px] leading-none truncate max-w-full ${selected ? "text-candle" : "text-cocoa-soft/80"}`}>
        <span className="sm:hidden">{shortHeb(d.hebrew_short)}</span><span className="hidden sm:inline">{d.hebrew_short}</span>
      </span>
      <span className="flex gap-0.5 h-1.5">{kinds.map((k) => <span key={k} className={`w-1.5 h-1.5 rounded-full ${selected ? "bg-candle" : DOT[k]}`} />)}</span>
    </button>
  );
}

export default function CalMes() {
  const now = new Date();
  const [ym, setYm] = useState([now.getFullYear(), now.getMonth() + 1]);
  const [month, setMonth] = useState(null);
  const [sel, setSel] = useState(localYmd());

  useEffect(() => {
    setMonth(null);
    calFetch(`/cal/month?y=${ym[0]}&m=${ym[1]}`).then(setMonth).catch((e) => toast.error(e.message));
  }, [ym]);

  const shift = (n) => setYm(([y, m]) => { const d = new Date(y, m - 1 + n, 1); return [d.getFullYear(), d.getMonth() + 1]; });
  const selDay = month?.days.find((d) => d.date === sel);
  const pad = month ? month.days[0].weekday : 0;

  return (
    <CalLayout>
      <div className="flex items-center justify-between mb-5">
        <button data-testid="cal-month-prev" onClick={() => shift(-1)} className="p-2.5 rounded-full bg-cream border border-line cal-press" aria-label="Mes anterior"><ChevronLeft className="w-5 h-5 text-cocoa" /></button>
        <div className="text-center">
          <h2 data-testid="cal-month-label" className="font-display italic text-cocoa text-3xl capitalize leading-tight">{month?.month_label || "…"}</h2>
          <p data-testid="cal-month-hebrew" className="text-sm text-dusk font-medium">{month?.hebrew_label}</p>
        </div>
        <button data-testid="cal-month-next" onClick={() => shift(1)} className="p-2.5 rounded-full bg-cream border border-line cal-press" aria-label="Mes siguiente"><ChevronRight className="w-5 h-5 text-cocoa" /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 sm:gap-1.5 mb-1.5">
        {WD.map((w) => <p key={w} className="text-center text-[10px] uppercase tracking-wider text-cocoa-soft">{w}</p>)}
      </div>
      <div className="grid grid-cols-7 gap-1 sm:gap-1.5 mb-6" data-testid="cal-month-grid">
        {Array.from({ length: pad }).map((_, i) => <span key={`p${i}`} />)}
        {month ? month.days.map((d) => <DayCell key={d.date} d={d} selected={d.date === sel} today={d.date === localYmd()} onClick={() => setSel(d.date)} />)
          : Array.from({ length: 35 }).map((_, i) => <span key={i} className="aspect-square rounded-2xl bg-cream/60 animate-pulse" />)}
      </div>
      {selDay && (
        <section data-testid="cal-selected-day" className="rounded-3xl bg-cream border border-line p-5">
          <p className="font-display text-cocoa text-2xl">{new Date(`${sel}T12:00:00`).toLocaleDateString("es-CL", { weekday: "long", day: "numeric", month: "long" })}</p>
          <p className="text-sm text-dusk mb-3">{selDay.hebrew_short}</p>
          {selDay.events.length ? (
            <div className="divide-y divide-line/70">
              {selDay.events.map((ev, i) => (
                <div key={i}>
                  <EventRow ev={ev} testId={`cal-sel-event-${i}`} />
                  {ev.kind === "cumple" && <div className="pl-11 -mt-1 pb-2"><CalBadge calendar={ev.calendar} /></div>}
                </div>
              ))}
            </div>
          ) : <p className="text-sm italic text-cocoa-soft">Un día tranquilo, sin fechas especiales.</p>}
          {selDay.events.some((e) => e.wish_key) && (
            <Link to={`/calendario?fecha=${sel}`} data-testid="cal-open-day" className="mt-4 inline-flex rounded-full bg-night text-white px-4 py-2 text-sm font-semibold cal-press">
              Ver el día y mandar saludos
            </Link>
          )}
        </section>
      )}
    </CalLayout>
  );
}
